import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/firebase/api-client";
import { Store } from "@/types";
import { Plus, Edit2, Trash2, Users, Store as StoreIcon, User, Mail, Phone, Filter, Key, ArrowLeft, UserCircle } from "lucide-react";
import { ConfirmModal } from "@/components/shared/ConfirmModal";
import { AlertModal } from "@/components/shared/AlertModal";
import { ChangePasswordModal } from "@/components/shared/ChangePasswordModal";
import { useModal } from "@/hooks/useModal";
import Loading from "@/components/shared/Loading";

interface Collaborateur {
  id: string;
  userId: string;
  storeId: string;
  store: { id: string; name: string } | null;
  user: {
    id: string;
    username: string;
    name: string;
    email?: string;
    phone?: string;
    isActive: boolean;
  } | null;
  createdAt: any;
  updatedAt: any;
}

export default function CollaborateursPage() {
  const [collaborateurs, setCollaborateurs] = useState<Collaborateur[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStore, setSelectedStore] = useState<string>("");
  const [showForm, setShowForm] = useState(false);
  const [editingCollaborateur, setEditingCollaborateur] = useState<Collaborateur | null>(null);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    name: "",
    storeId: "",
    email: "",
    phone: "",
    // Jour de repos (15/10/2026) : "" = aucun, "0" = dimanche … "6" = samedi.
    jourRepos: "",
  });

  // Modales
  const deleteModal = useModal();
  const successModal = useModal();
  const errorModal = useModal();
  const changePasswordModal = useModal();
  const [deleteCollaborateur, setDeleteCollaborateur] = useState<{ id: string; name: string } | null>(null);
  const [changePasswordUser, setChangePasswordUser] = useState<{ id: string; name: string } | null>(null);
  const [modalMessage, setModalMessage] = useState({ title: "", message: "" });
  const [changingPassword, setChangingPassword] = useState(false);

  // Charger les données
  useEffect(() => {
    loadData();
  }, [selectedStore]);

  async function loadData() {
    try {
      setLoading(true);
      const [collabsData, storesData] = await Promise.all([
        api.collaborateurs.getAll(selectedStore ? { storeId: selectedStore } : undefined).catch(() => []),
        api.stores.getAll().catch(() => []),
      ]);
      setCollaborateurs(collabsData || []);
      setStores(storesData || []);
    } catch (error) {
      console.error("Error loading data:", error);
      setModalMessage({
        title: "Erreur",
        message: "Erreur lors du chargement des données",
      });
      errorModal.open();
    } finally {
      setLoading(false);
    }
  }

  // Réinitialiser le formulaire
  function resetForm() {
    setFormData({
      username: "",
      password: "",
      name: "",
      storeId: "",
      email: "",
      phone: "",
      jourRepos: "",
    });
    setEditingCollaborateur(null);
    setShowForm(false);
  }

  // Ouvrir formulaire de création
  function handleCreate() {
    resetForm();
    setShowForm(true);
  }

  // Ouvrir formulaire d'édition
  function handleEdit(collab: Collaborateur) {
    if (!collab.user) return;
    setFormData({
      username: collab.user.username,
      password: "",
      name: collab.user.name,
      storeId: collab.storeId,
      email: collab.user.email || "",
      phone: collab.user.phone || "",
      jourRepos:
        typeof (collab as any).jourRepos === "number" ? String((collab as any).jourRepos) : "",
    });
    setEditingCollaborateur(collab);
    setShowForm(true);
  }

  // Soumettre le formulaire
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      if (editingCollaborateur) {
        const updateData: any = {
          phone: formData.phone || null,
          jourRepos: formData.jourRepos === "" ? null : Number(formData.jourRepos),
        };
        if (formData.name !== editingCollaborateur.user?.name) {
          updateData.name = formData.name;
        }
        await api.collaborateurs.update(editingCollaborateur.id, updateData);
        setModalMessage({
          title: "Succès",
          message: "Collaborateur modifié avec succès",
        });
        successModal.open();
      } else {
        if (!formData.password) {
          setModalMessage({
            title: "Erreur",
            message: "Le mot de passe est obligatoire",
          });
          errorModal.open();
          return;
        }
        await api.collaborateurs.create({
          ...formData,
          email: formData.email?.trim() || undefined,
          phone: formData.phone?.trim() || undefined,
          jourRepos: formData.jourRepos === "" ? null : Number(formData.jourRepos),
        });
        setModalMessage({
          title: "Succès",
          message: "Collaborateur créé avec succès",
        });
        successModal.open();
      }
      resetForm();
      loadData();
    } catch (error: any) {
      console.error("Error saving collaborateur:", error);
      setModalMessage({
        title: "Erreur",
        message: error.message || "Erreur lors de la sauvegarde",
      });
      errorModal.open();
    }
  }

  // Ouvrir modal de suppression
  function handleDeleteClick(collab: Collaborateur) {
    setDeleteCollaborateur({ id: collab.userId || collab.id, name: collab.user?.name || "" });
    deleteModal.open();
  }

  // Confirmer suppression
  async function handleDeleteConfirm() {
    if (!deleteCollaborateur) return;

    try {
      await api.collaborateurs.delete(deleteCollaborateur.id);
      deleteModal.close();
      setModalMessage({
        title: "Succès",
        message: "Collaborateur supprimé avec succès",
      });
      successModal.open();
      loadData();
    } catch (error: any) {
      console.error("Error deleting collaborateur:", error);
      deleteModal.close();
      setModalMessage({
        title: "Erreur",
        message: error.message || "Erreur lors de la suppression",
      });
      errorModal.open();
    }
  }

  // Ouvrir modal changement de mot de passe
  function handleChangePasswordClick(collab: Collaborateur) {
    setChangePasswordUser({ id: collab.userId || collab.id, name: collab.user?.name || "" });
    changePasswordModal.open();
  }

  // Confirmer changement de mot de passe
  async function handleChangePasswordConfirm(newPassword: string) {
    if (!changePasswordUser) return;

    try {
      setChangingPassword(true);
      await api.auth.changePassword(changePasswordUser.id, newPassword);
      setModalMessage({
        title: "Succès",
        message: "Mot de passe modifié avec succès",
      });
      successModal.open();
    } catch (error: any) {
      console.error("Error changing password:", error);
      throw error; // La modale gérera l'erreur
    } finally {
      setChangingPassword(false);
    }
  }

  // Filtrer les collaborateurs affichés
  const displayedCollaborateurs = selectedStore
    ? collaborateurs.filter((c) => c.storeId === selectedStore)
    : collaborateurs;

  if (loading) {
    return (
      <div className="p-4 md:p-6">
        <Loading />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="mb-4 md:mb-6">
        <Link
          to="/admin/users"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-700 hover:text-gray-900 text-sm font-medium transition-colors duration-200 border border-gray-200 hover:border-gray-300"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Retour aux Utilisateurs</span>
        </Link>
      </div>
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4 md:mb-6">
        <h1 className="text-xl md:text-2xl font-bold">Gestion des Collaborateurs</h1>
        <button
          onClick={handleCreate}
          className="bg-primary text-white px-4 py-2 rounded hover:bg-primary/90 flex items-center justify-center gap-2 min-h-[44px] w-full sm:w-auto"
        >
          <Plus className="w-5 h-5" />
          <span>Nouveau Collaborateur</span>
        </button>
      </div>

      {/* Filtre par store */}
      <div className="bg-white p-4 rounded-lg shadow mb-4 md:mb-6">
        <label className="block text-sm font-medium mb-2 flex items-center gap-2">
          <Filter className="w-4 h-4" />
          Filtrer par Store
        </label>
        <select
          value={selectedStore}
          onChange={(e) => setSelectedStore(e.target.value)}
          className="w-full px-3 py-2 border rounded min-h-[44px]"
        >
          <option value="">Tous les stores</option>
          {stores.map((store) => (
            <option key={store.id} value={store.id}>
              {store.name}
            </option>
          ))}
        </select>
        {selectedStore && (
          <p className="mt-2 text-sm text-gray-600">
            {displayedCollaborateurs.length} collaborateur(s) pour ce store
          </p>
        )}
      </div>

      {/* Formulaire */}
      {showForm && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 mb-4 md:mb-6 overflow-hidden">
          {/* Header du formulaire */}
          <div className="bg-gradient-to-r from-blue-50 to-blue-100/50 px-5 md:px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg md:text-xl font-bold text-gray-800 flex items-center gap-2">
              <UserCircle className="w-5 h-5 text-blue-600" />
              {editingCollaborateur ? "Modifier le Collaborateur" : "Nouveau Collaborateur"}
            </h2>
          </div>
          
          {/* Contenu du formulaire */}
          <form onSubmit={handleSubmit} className="p-5 md:p-6 space-y-5">
            {/* Section Authentification */}
            <div className="space-y-4 pb-4 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Authentification
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">
                    Nom d'utilisateur <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    disabled={!!editingCollaborateur}
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all disabled:bg-gray-100 disabled:cursor-not-allowed min-h-[44px]"
                    placeholder="ex: ahmed_maarif"
                  />
                  {editingCollaborateur && (
                    <p className="mt-1.5 text-xs text-gray-500 flex items-center gap-1">
                      Le nom d'utilisateur ne peut pas être modifié
                    </p>
                  )}
                </div>

                {!editingCollaborateur && (
                  <div>
                    <label className="block text-sm font-medium mb-2 text-gray-700">
                      Mot de passe <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      required
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all min-h-[44px]"
                      placeholder="Minimum 6 caractères"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Section Informations personnelles */}
            <div className="space-y-4 pb-4 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Informations personnelles
              </h3>
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  Nom complet <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all min-h-[44px]"
                  placeholder="Ex: Ahmed Benali"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-gray-700">
                  Store <span className="text-red-500">*</span>
                </label>
                <select
                  required
                  disabled={!!editingCollaborateur}
                  value={formData.storeId}
                  onChange={(e) => setFormData({ ...formData, storeId: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all disabled:bg-gray-100 disabled:cursor-not-allowed min-h-[44px]"
                >
                  <option value="">Sélectionner un store</option>
                  {stores
                    // Plusieurs responsables par magasin depuis le 15/10/2026 :
                    // les commandes sont reparties entre eux.
                    .filter((s) => s.isActive)
                    .map((store) => (
                      <option key={store.id} value={store.id}>
                        {store.name}
                      </option>
                    ))}
                </select>
                {editingCollaborateur && (
                  <p className="mt-1.5 text-xs text-gray-500">
                    Le store ne peut pas être modifié
                  </p>
                )}
                {!editingCollaborateur && formData.storeId && collaborateurs.some((c) => c.storeId === formData.storeId) && (
                  <p className="mt-1.5 text-sm text-gray-600 flex items-center gap-1">
                    Ce magasin a déjà un responsable : les commandes seront réparties entre eux.
                  </p>
                )}
              </div>
            </div>

            {/* JOUR DE REPOS (15/10/2026) : ce jour-la, l'autre responsable du
                magasin recoit toutes les commandes. */}
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Jour de repos</label>
              <select
                value={formData.jourRepos}
                onChange={(e) => setFormData({ ...formData, jourRepos: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all min-h-[44px]"
              >
                <option value="">Aucun</option>
                <option value="1">Lundi</option>
                <option value="2">Mardi</option>
                <option value="3">Mercredi</option>
                <option value="4">Jeudi</option>
                <option value="5">Vendredi</option>
                <option value="6">Samedi</option>
                <option value="0">Dimanche</option>
              </select>
              <p className="mt-1.5 text-xs text-gray-500">
                Ce jour-là, l'autre responsable du magasin reçoit toutes les commandes.
              </p>
            </div>

            {/* Section Contact */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
                Contact (optionnel)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">Email (optionnel)</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all min-h-[44px]"
                    placeholder="ex: ahmed@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-700">Téléphone</label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all min-h-[44px]"
                    placeholder="+212600000000"
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-3 pt-6 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  type="submit"
                  className="flex-1 bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 min-h-[44px] font-semibold transition-colors duration-200 shadow-sm hover:shadow-md"
                >
                  {editingCollaborateur ? "Modifier le Collaborateur" : "Créer le Collaborateur"}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 sm:flex-initial bg-white text-gray-700 px-6 py-3 rounded-lg border border-gray-300 hover:bg-gray-50 min-h-[44px] font-medium transition-colors duration-200"
                >
                  Annuler
                </button>
              </div>
              {editingCollaborateur && (
                <button
                  type="button"
                  onClick={() => handleChangePasswordClick(editingCollaborateur)}
                  className="w-full flex items-center justify-center gap-2 text-gray-700 hover:text-gray-900 hover:bg-gray-50 text-sm py-2.5 border border-gray-300 rounded-lg min-h-[44px] font-medium transition-colors duration-200"
                >
                  <Key className="w-4 h-4" />
                  Changer le mot de passe
                </button>
              )}
            </div>
          </form>
        </div>
      )}

      {/* Liste des collaborateurs - Mobile: Cards */}
      <div className="md:hidden space-y-4">
        {displayedCollaborateurs.length === 0 ? (
          <div className="bg-white p-8 rounded-lg shadow text-center text-gray-500">
            <Users className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p>
              {selectedStore
                ? "Aucun collaborateur pour ce store"
                : "Aucun collaborateur trouvé"}
            </p>
          </div>
        ) : (
          displayedCollaborateurs.map((collab) => (
            <div key={collab.id} className="bg-white p-4 rounded-lg shadow">
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <h3 className="font-semibold text-lg mb-2">{collab.user?.name || "-"}</h3>
                  <div className="space-y-1 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <StoreIcon className="w-4 h-4 flex-shrink-0" />
                      <span>{collab.store?.name || `Store ID: ${collab.storeId}`}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 flex-shrink-0" />
                      <span>{collab.user?.username || "-"}</span>
                    </div>
                    {collab.user?.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 flex-shrink-0" />
                        <span>{collab.user.email}</span>
                      </div>
                    )}
                    {collab.user?.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 flex-shrink-0" />
                        <span>{collab.user.phone}</span>
                      </div>
                    )}
                  </div>
                </div>
                <span
                  className={`px-2 py-1 rounded text-xs flex-shrink-0 ${
                    collab.user?.isActive
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {collab.user?.isActive ? "Actif" : "Inactif"}
                </span>
              </div>
              <div className="flex gap-2 pt-3 border-t">
                <button
                  onClick={() => handleEdit(collab)}
                  className="flex-1 flex items-center justify-center gap-2 text-blue-600 hover:text-blue-800 text-sm py-2 border border-blue-600 rounded min-h-[44px] font-medium"
                >
                  <Edit2 className="w-4 h-4" />
                  Modifier
                </button>
                <button
                  onClick={() => handleDeleteClick(collab)}
                  className="flex-1 flex items-center justify-center gap-2 text-red-600 hover:text-red-800 text-sm py-2 border border-red-600 rounded min-h-[44px] font-medium"
                >
                  <Trash2 className="w-4 h-4" />
                  Supprimer
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop: Table */}
      <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold">Nom</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Store</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Username</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Email</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Statut</th>
                <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {displayedCollaborateurs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    {selectedStore
                      ? "Aucun collaborateur pour ce store"
                      : "Aucun collaborateur trouvé"}
                  </td>
                </tr>
              ) : (
                displayedCollaborateurs.map((collab) => (
                  <tr key={collab.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      {collab.user?.name || "-"}
                      {typeof (collab as any).jourRepos === "number" && (
                        <span className="block text-xs text-gray-500">
                          repos :{" "}
                          {["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"][(collab as any).jourRepos]}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {collab.store?.name || `Store ID: ${collab.storeId}`}
                    </td>
                    <td className="px-4 py-3 text-sm">{collab.user?.username || "-"}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {collab.user?.email || "-"}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded text-xs ${
                          collab.user?.isActive
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {collab.user?.isActive ? "Actif" : "Inactif"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(collab)}
                          className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                        >
                          <Edit2 className="w-4 h-4" />
                          Modifier
                        </button>
                        <button
                          onClick={() => handleDeleteClick(collab)}
                          className="text-red-600 hover:text-red-800 text-sm flex items-center gap-1"
                        >
                          <Trash2 className="w-4 h-4" />
                          Supprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modales */}
      <ConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={deleteModal.close}
        onConfirm={handleDeleteConfirm}
        title="Supprimer le Collaborateur"
        message={`Êtes-vous sûr de vouloir supprimer le collaborateur "${deleteCollaborateur?.name}" ? Cette action est irréversible.`}
        type="danger"
        confirmText="Supprimer"
      />

      <AlertModal
        isOpen={successModal.isOpen}
        onClose={successModal.close}
        title={modalMessage.title}
        message={modalMessage.message}
        type="success"
      />

      <AlertModal
        isOpen={errorModal.isOpen}
        onClose={errorModal.close}
        title={modalMessage.title}
        message={modalMessage.message}
        type="error"
      />

      <ChangePasswordModal
        isOpen={changePasswordModal.isOpen}
        onClose={changePasswordModal.close}
        onConfirm={handleChangePasswordConfirm}
        userName={changePasswordUser?.name || ""}
        loading={changingPassword}
      />
    </div>
  );
}
